// # Photo Upload Signup Component (Step 4)
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../auth/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import AuthHeader from '../../components/AuthHeader';
import { FaCheckCircle } from 'react-icons/fa';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { LogoFade } from './more_components/auth_animation';
import SignupProgress from './more_components/progress_bar';
import TermsPrivacyModal from './more_components/TermsPrivacyModal';
import loginOutManager from '../../../auth/login_out_manager';
import sessionManager from '../../../auth/session';
import Cookies from 'js-cookie';
// # Import email verification functions
import { sendVerificationEmail } from '../../../auth/verify/verify_user_email';
// Replace import './signup.css' with:
import './styles/common.css';
import './styles/photo.css';

function SignupPhoto() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            }));
          }, 'image/jpeg', 0.7); // 70% quality
        };
      };
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      if (compressedFile.size > 1024 * 1024) {
        setError('Image size should be less than 1MB');
        return;
      }

      setSelectedFile(compressedFile);
      setPreview(URL.createObjectURL(compressedFile));
      setError('');
    } catch (error) {
      console.error('Error handling file:', error);
      setError('Error processing image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  // # Check for existing valid session on mount (replace authCookieManager with direct cookie checks)
  const hasValidAuthCookies = () => {
    return !!(
      Cookies.get('authToken') &&
      Cookies.get('deviceId') &&
      Cookies.get('sessionActive')
    );
  };

  // # Set authentication tokens (replace authCookieManager with direct cookie setting)
  const setAuthTokens = async (idToken, deviceId) => {
    try {
      // # Set auth token cookie
      Cookies.set('authToken', idToken, {
        expires: 7, // 7 days
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      // # Set device ID cookie
      Cookies.set('deviceId', deviceId, {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      // # Set session active cookie
      Cookies.set('sessionActive', 'true', {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      return true;
    } catch (error) {
      console.error('Error setting auth cookies:', error);
      return false;
    }
  };

  // # Clear auth cookies (replace authCookieManager with direct cookie removal)
  const clearAuthCookies = async () => {
    try {
      Cookies.remove('authToken', { path: '/' });
      Cookies.remove('deviceId', { path: '/' });
      Cookies.remove('sessionActive', { path: '/' });
      return true;
    } catch (error) {
      console.error('Error clearing auth cookies:', error);
      return false;
    }
  };

  const handleSignOut = async () => {
    try {
      await Promise.all([
        auth.signOut(),
        sessionManager.clearSession(),
        clearAuthCookies() // Use the local function instead
      ]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleTermsConfirm = async (accepted) => {
    if (!accepted) return;
    
    setShowTermsModal(false);
    setUploading(true);
    
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      let photoURL = '/icons/pfp.png'; // Default photo URL

      if (selectedFile) {
        const storage = getStorage();
        const photoRef = ref(storage, `avatars/${user.uid}/profile.jpg`);
        
        await uploadBytes(photoRef, selectedFile);
        photoURL = await getDownloadURL(photoRef);
      }
      
      await updateDoc(doc(db, 'users', user.uid), {
        photoURL,
        isProfileComplete: true,
        signupStep: 5, // Completed
        accepted_terms_privacy: true,
        emailVerified: false, // # Initialize email verification status
        'metadata.updatedAt': new Date().toISOString(),
        'metadata.terms_accepted_at': new Date().toISOString()
      });

      // # Send verification email
      await sendVerificationEmail(user);

      setShowForm(false);
      setSuccess(true);
      
      // # Store credentials before signing out
      const email = user.email;
      const password = localStorage.getItem('tempPassword');
      
      // # Clean up localStorage
      localStorage.removeItem('personalData');
      localStorage.removeItem('lastSignupStep');
      localStorage.removeItem('tempPassword');

      // # Sign out current session
      await auth.signOut();
      
      // # Add delay for state updates
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (email && password) {
        let retryCount = 0;
        const maxRetries = 3;
        let loginSuccess = false;

        while (retryCount < maxRetries && !loginSuccess) {
          try {
            // # Sign in with stored credentials
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            
            // # Generate device info
            const deviceInfo = {
              platform: navigator.platform,
              userAgent: navigator.userAgent,
              cores: navigator.hardwareConcurrency,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            };

            // # Generate device ID
            const deviceId = btoa(
              JSON.stringify({
                ...deviceInfo,
                timestamp: Date.now()
              })
            ).replace(/[/+=]/g, '').substring(0, 32);

            // # Get ID token
            const idToken = await userCredential.user.getIdToken();
            
            // # Set auth cookies with device info (use local function)
            const cookieResult = await setAuthTokens(idToken, deviceId);
            if (!cookieResult) {
              throw new Error('Failed to set authentication cookies');
            }

            // # Record sign in activity with device info
            await loginOutManager.recordSignInActivity(userCredential.user.uid);

            // # Initialize session with complete device info
            await sessionManager.initializeSession(userCredential.user, {
              forceRefresh: true,
              device_info: deviceInfo
            });

            loginSuccess = true;
            
            // # Navigate to welcome screen instead of dashboard
            navigate('/welcome', {
              state: {
                isAuthenticated: true,
                timestamp: Date.now(),
                isProfileComplete: true,
                redirectTo: '/dashboard'
              }
            });
            break;
          } catch (error) {
            console.error(`Attempt ${retryCount + 1}: Error signing in after signup:`, error);
            retryCount++;
            if (retryCount === maxRetries) {
              throw new Error('Failed to sign in after maximum retries');
            }
            // # Wait before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      // # Add delay before navigation
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/welcome', {
        state: {
          isAuthenticated: true,
          timestamp: Date.now(),
          isProfileComplete: true,
          redirectTo: '/dashboard'
        }
      });
    } catch (error) {
      console.error('Error completing signup:', error);
      setError('An error occurred while completing signup. Please try signing in manually.');
      setUploading(false);
      navigate('/signin');
    }
  };

  return (
    <div>
      <AuthHeader />
      <SignupProgress currentStep={4} />
      <div className="sgn_pht_signup-container signup-photo-step">
        <div className="sgn_pht_signup-photo-form-section">
          <h2>Add Profile Photo</h2>
          <p className="sgn_pht_photo-step-subtitle">This step is optional but recommended</p>
          
          {success ? (
            <div className="sgn_pht_success-container">
              <FaCheckCircle className="sgn_pht_success-icon" />
              <h3>Signup Successful!</h3>
              <p>Redirecting to dashboard...</p>
            </div>
          ) : showForm ? (
            <form onSubmit={handleSubmit} className="sgn_pht_signup-photo-form">
              <div 
                className="sgn_pht_signup-photo-container"
                onClick={() => fileInputRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="sgn_pht_signup-photo-preview" />
                ) : (
                  <div className="sgn_pht_signup-photo-placeholder">
                    <i className="fas fa-user"></i>
                  </div>
                )}
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  className="sgnp_cmn_hidden"
                  style={{ display: 'none' }}
                />
              </div>

              {error && <div className="sgn_pht_error-message">{error}</div>}

              <div className="sgn_pht_photo-step-buttons">
                <button 
                  type="submit"
                  className="sgnp_cmn_button"
                  disabled={uploading}
                >
                  {uploading ? 'Processing...' : 'Complete Signup'}
                </button>
              </div>
            </form>
          ) : (
            <div className="sgn_pht_processing-container">
              <p>Processing your registration...</p>
            </div>
          )}
        </div>
      </div>

      <TermsPrivacyModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onConfirm={handleTermsConfirm}
      />
    </div>
  );
}

export default SignupPhoto; 